import { useCallback, useMemo, useState } from "react";
import {
  generatePath,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";

import {
  FieldArray,
  MessageBar,
  MessageBarType,
  Stack,
  SubmissionForm,
  TopBarDefaultHeader,
  useTheme
} from "@bps/fluent-ui";
import { SectionTitle } from "@components/SectionTitle";
import { withPermissions } from "@components/withPermissions";
import {
  Rollout,
  RolloutCreateRequest,
  SoftwarePackageVersion
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  useCreateRollout,
  useUpdateRollout
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { IsDevelopmentEnvironment } from "@libs/config/config";
import { Permissions } from "@libs/permissions/permissions.enum";

import { convertToDateTime, hasRolloutOccurred } from "../utils";
import { confirmRolloutPast } from "./confirmRolloutPast";
import { RolloutForm } from "./RolloutForm";
import {
  rolloutFormSchema,
  rolloutFormValidator
} from "./RolloutFormValidator";
import { parseRollout, RolloutFormValues } from "./rolloutFormValues";

export const DUPLICATE_ROLLOUT_PARAM_NAME = "duplicate";

interface RolloutHostBaseProps {
  rollout?: Rollout;
  softwarePackageVersionsForRollout?: SoftwarePackageVersion[];
  defaultName?: string;
  modeLabel: "Edit" | "New";
}

const RolloutHostBase: React.FC<RolloutHostBaseProps> = ({
  rollout: existingRollout,
  softwarePackageVersionsForRollout,
  defaultName,
  modeLabel
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { ringId } = useParams<{
    ringId: string;
  }>();

  const [error, setError] = useState<string>();

  const backToRings = useCallback(() => {
    const to = generatePath("/system/plt/deployment-rings/:ringId", {
      ringId
    });
    navigate(to);
  }, [navigate, ringId]);

  const { mutateAsync: createRollout } = useCreateRollout();
  const { mutateAsync: updateRollout } = useUpdateRollout();
  const [queryParams] = useSearchParams();

  const isDuplicate = !!queryParams.get(DUPLICATE_ROLLOUT_PARAM_NAME);

  const rollout = useMemo(() => {
    let editRollout = existingRollout;
    if (isDuplicate && existingRollout) {
      const { id, ...duplicatedRollout } = existingRollout;
      editRollout = duplicatedRollout;
    }

    return (
      editRollout ??
      ({
        deploymentRingId: ringId!,
        isActive: true,
        displayName: defaultName,
        rolloutPackages: []
      } as Rollout)
    );
  }, [existingRollout, isDuplicate, ringId, defaultName]);

  const isReadOnly =
    hasRolloutOccurred(rollout) && !IsDevelopmentEnvironment && !isDuplicate;

  const onSubmit = async (values: RolloutFormValues) => {
    const {
      isActive,
      initialDownloadDateStartUtc,
      initialDownloadDateEndUtc,
      initialDownloadTimeStartUtc,
      initialDownloadTimeEndUtc,
      initialInstallDateStartUtc,
      initialInstallDateEndUtc,
      initialInstallTimeStartUtc,
      initialInstallTimeEndUtc,
      rolloutPackages,
      ...rest
    } = values;

    const downloadStartUtc = convertToDateTime(
      initialDownloadDateStartUtc!,
      initialDownloadTimeStartUtc!
    );

    const downloadEndUtc = convertToDateTime(
      initialDownloadDateEndUtc!,
      initialDownloadTimeEndUtc!
    );

    const installStartUtc = convertToDateTime(
      initialInstallDateStartUtc!,
      initialInstallTimeStartUtc!
    );

    const installEndUtc = convertToDateTime(
      initialInstallDateEndUtc!,
      initialInstallTimeEndUtc!
    );

    const request = {
      ...rest,
      isActive: isActive!,
      deploymentRingId: ringId!,
      initialDownloadStartUtc: downloadStartUtc.toISO(),
      initialDownloadEndUtc: downloadEndUtc.toISO(),
      initialInstallStartUtc: installStartUtc.toISO(),
      initialInstallEndUtc: installEndUtc.toISO(),
      rolloutPackages:
        rolloutPackages?.map(rolloutPackage => {
          return {
            rolloutPackageId: rolloutPackage.rolloutPackageId,
            softwarePackageVersionId: rolloutPackage.softwarePackageVersionId,
            eTag: rolloutPackage.eTag
          };
        }) ?? [],
      rolloutPackagesDesiredConfigs: rolloutPackages
        ?.map(
          rolloutPackage =>
            rolloutPackage.desiredConfigs?.map(desiredConfig => {
              return {
                rolloutPackageDesiredConfigId: desiredConfig.id,
                key: desiredConfig.key,
                value: desiredConfig.value,
                softwarePackageVersionId: rolloutPackages.find(
                  x => x.softwarePackageId === desiredConfig.softwarePackageId
                )?.softwarePackageVersionId,
                eTag: desiredConfig.eTag
              };
            }) ?? []
        )
        .flat()
    } as RolloutCreateRequest;

    setError(undefined);

    await confirmRolloutPast({
      downloadStartUtc,
      async changeState() {
        try {
          if (!rollout.id) {
            await createRollout(request);
          } else {
            await updateRollout({
              ...request,
              rolloutId: rollout.id
            });
          }

          backToRings();
        } catch (error) {
          setError(error.detail ?? error.message);
        }
      }
    });
  };

  return (
    <SubmissionForm<RolloutFormValues>
      initialValues={parseRollout(rollout, softwarePackageVersionsForRollout)}
      onSubmit={onSubmit}
      validate={values =>
        rolloutFormValidator.validate(values, rolloutFormSchema)
      }
      hideButtons
      styles={{ root: { flexGrow: 1 }, fields: { height: "100%" } }}
    >
      {() => (
        <Stack verticalFill>
          <Stack
            verticalFill
            styles={{
              root: { padding: theme.spacing.s1, width: "100%" }
            }}
            tokens={{ childrenGap: theme.spacing.m }}
          >
            <Stack horizontal>
              <TopBarDefaultHeader
                backButtonOnClick={backToRings}
                heading="Back to rings"
              />
              <SectionTitle>{modeLabel} Rollout</SectionTitle>
            </Stack>

            {error && (
              <MessageBar messageBarType={MessageBarType.error}>
                {error}
              </MessageBar>
            )}

            {isReadOnly && (
              <MessageBar
                messageBarType={MessageBarType.warning}
                dismissButtonAriaLabel="Close"
              >
                This rollout has already occurred and cannot be updated. To
                update software packages, create a new rollout.
              </MessageBar>
            )}

            <FieldArray name="rolloutPackages">
              {() => <RolloutForm isReadOnly={isReadOnly} />}
            </FieldArray>
          </Stack>
        </Stack>
      )}
    </SubmissionForm>
  );
};

export const RolloutHost = withPermissions(
  RolloutHostBase,
  [
    Permissions.PltFieldManagementRead,
    Permissions.PltFieldManagementWrite,
    Permissions.PltFieldManagementDeploy
  ],
  "or"
);
